/**
 * ActiveComputePanel - Sidebar compute session carousel
 *
 * Each instance is its own self-contained tile (border, gradient, status color).
 * The entire tile slides in/out as a unit.
 *
 * Navigation: arrow buttons, dot indicators, trackpad horizontal swipe,
 * touch/pointer drag with live feedback.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCpu, FiExternalLink, FiSquare, FiClock, FiDollarSign, FiLoader, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getApiUrl } from '../../utils/api';
import { getInstanceDisplayState, getStatusVisuals, type InstanceDisplayState } from '../../utils/instanceStatus';

export interface ActiveInstance {
  instance_id: string;
  provider: string;
  instance_type: string;
  region: string;
  gpu_type: string;
  launch_time: string;
  hourly_cost: number;
  status: string;
  public_ip?: string;
  terminal_ready?: boolean | null;
}

interface ActiveComputePanelProps {
  instances: ActiveInstance[];
  isCollapsed?: boolean;
  onInstanceTerminated?: () => void;
}

function getTileColors(displayState: InstanceDisplayState) {
  switch (displayState) {
    case 'terminating':
      return 'from-red-950/50 to-gray-900 border-red-800/40';
    case 'running':
      return 'from-green-950/50 to-gray-900 border-green-800/40';
    default:
      return 'from-blue-950/50 to-gray-900 border-blue-800/40';
  }
}

function getDotColor(displayState: InstanceDisplayState) {
  switch (displayState) {
    case 'terminating': return 'bg-red-400';
    case 'running': return 'bg-green-400';
    default: return 'bg-blue-400';
  }
}

// ── Per-instance tile (self-contained with its own border/gradient) ──
function InstanceTile({
  instance,
  terminatingId,
  showConfirmId,
  onOpenNotebook,
  onStop,
}: {
  instance: ActiveInstance;
  terminatingId: string | null;
  showConfirmId: string | null;
  onOpenNotebook: (id: string) => void;
  onStop: (id: string) => void;
}) {
  const [runningSeconds, setRunningSeconds] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);

  useEffect(() => {
    const calc = () => {
      if (instance.launch_time) {
        const s = Math.max(0, Math.floor((Date.now() - new Date(instance.launch_time).getTime()) / 1000));
        setRunningSeconds(s);
      }
    };
    calc();
    const iv = setInterval(calc, 1000);
    return () => clearInterval(iv);
  }, [instance.launch_time]);

  useEffect(() => {
    const timeCost = (runningSeconds / 3600) * (instance.hourly_cost || 0);
    const minCharge = (instance.hourly_cost || 0) * (10 / 60);
    setEstimatedCost(Math.max(timeCost, minCharge));
  }, [runningSeconds, instance.hourly_cost]);

  const displayState: InstanceDisplayState = terminatingId === instance.instance_id
    ? 'terminating'
    : getInstanceDisplayState(instance.status, instance.terminal_ready);
  const visuals = getStatusVisuals(displayState);
  const isTerminating = displayState === 'terminating';
  const isReady = displayState === 'running';
  const tileColors = getTileColors(displayState);

  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  return (
    <div className="w-full flex-shrink-0">
      <div className={`rounded-lg bg-gradient-to-br border overflow-hidden ${tileColors}`}>
        <div className="px-3 pb-3 pt-2">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                isTerminating ? 'bg-red-400' : isReady ? 'bg-green-400' : 'bg-blue-400'
              }`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                isTerminating ? 'bg-red-500' : isReady ? 'bg-green-500' : 'bg-blue-500'
              }`} />
            </span>
            <span className={`text-xs font-semibold uppercase tracking-wider ${
              isTerminating ? 'text-red-400' : isReady ? 'text-green-400' : 'text-blue-400'
            }`}>
              {isTerminating ? 'Terminating...' : isReady ? 'Active Session' : visuals.label}
            </span>
            <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded ${
              instance.provider === 'aws' ? 'bg-orange-500/15 text-orange-400/80'
              : instance.provider === 'azure' ? 'bg-cyan-500/15 text-cyan-400/80'
              : 'bg-blue-500/15 text-blue-400/80'
            }`}>
              {instance.provider.toUpperCase()}
            </span>
          </div>

          {/* GPU */}
          <div className="flex items-center gap-2 mb-2">
            <FiCpu className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-medium text-white truncate">
              {instance.gpu_type || instance.instance_type}
            </span>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
            <span className="flex items-center gap-1"><FiDollarSign className="w-3 h-3" />${instance.hourly_cost.toFixed(2)}/hr</span>
            <span className="text-gray-600">&bull;</span>
            <span className="flex items-center gap-1"><FiClock className="w-3 h-3" />{fmtTime(runningSeconds)}</span>
          </div>

          {/* Cost */}
          <div className="bg-gray-900/50 rounded px-2 py-1.5 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Est. cost</span>
              <span className="text-sm font-mono font-medium text-white">${estimatedCost.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={isReady && !isTerminating ? () => onOpenNotebook(instance.instance_id) : undefined}
              disabled={!isReady || isTerminating}
              className={`flex-1 min-w-0 px-2 py-1.5 text-xs font-medium rounded transition-colors flex items-center justify-center gap-1.5 ${
                isTerminating ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : isReady ? 'bg-green-600 hover:bg-green-500 text-white cursor-pointer'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isTerminating ? (
                <FiLoader className="w-3 h-3 animate-spin flex-shrink-0" />
              ) : isReady ? (
                <><FiExternalLink className="w-3 h-3 flex-shrink-0" /><span className="truncate">Open</span></>
              ) : (
                <><FiLoader className="w-3 h-3 animate-spin flex-shrink-0" /><span className="truncate">Starting...</span></>
              )}
            </button>
            <button
              onClick={() => onStop(instance.instance_id)}
              disabled={isTerminating}
              className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center justify-center gap-1.5 ${
                showConfirmId === instance.instance_id
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
              } ${isTerminating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isTerminating ? (
                <div className="w-3 h-3 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin flex-shrink-0" />
              ) : (
                <FiSquare className="w-3 h-3 flex-shrink-0" />
              )}
              <span className="truncate">{showConfirmId === instance.instance_id ? 'Sure?' : 'Stop'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main carousel panel ─────────────────────────────────────────────
export default function ActiveComputePanel({
  instances,
  isCollapsed = false,
  onInstanceTerminated,
}: ActiveComputePanelProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const [showConfirmStop, setShowConfirmStop] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const isTransitioning = useRef(false);

  // Refs for stable closures — handlers never go stale
  const countRef = useRef(0);
  const indexRef = useRef(0);
  const accDeltaX = useRef(0);
  const wheelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sort: ready > starting > launching > terminating
  const sorted = [...instances].sort((a, b) => {
    const rank = (i: ActiveInstance) => {
      if (i.status === 'running' && i.terminal_ready === true) return 0;
      if (i.status === 'running') return 1;
      if (i.status === 'pending' || i.status === 'launching') return 2;
      if (i.status === 'terminating') return 3;
      return 4;
    };
    return rank(a) - rank(b);
  });

  const count = sorted.length;
  countRef.current = count;
  indexRef.current = currentIndex;

  // Clamp index when instances are removed
  useEffect(() => {
    if (currentIndex >= count) {
      const clamped = Math.max(0, count - 1);
      indexRef.current = clamped;
      setCurrentIndex(clamped);
    }
  }, [count, currentIndex]);

  // ── Stable navigation — zero deps, uses refs for latest values ──
  const slide = useCallback((direction: 1 | -1) => {
    const c = countRef.current;
    const cur = indexRef.current;
    const next = cur + direction;
    // Clamp at edges — no wrap-around (avoids reverse animation)
    if (next < 0 || next >= c || isTransitioning.current) return;
    isTransitioning.current = true;
    setTimeout(() => { isTransitioning.current = false; }, 350);
    indexRef.current = next;
    setCurrentIndex(next);
    setShowConfirmStop(null);
    setDragOffset(0);
  }, []);

  const goTo = useCallback((idx: number) => {
    if (idx === indexRef.current || isTransitioning.current) return;
    isTransitioning.current = true;
    setTimeout(() => { isTransitioning.current = false; }, 350);
    indexRef.current = idx;
    setCurrentIndex(idx);
    setShowConfirmStop(null);
    setDragOffset(0);
  }, []);

  // ── Trackpad wheel → horizontal slide ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el || count <= 1) return;

    const onWheel = (e: WheelEvent) => {
      // During transition, drain inertia so it doesn't fire after lock expires
      if (isTransitioning.current) {
        accDeltaX.current = 0;
        // Still prevent default to stop sidebar scroll / browser back-forward
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      // Only act on horizontal-dominant gestures
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (Math.abs(e.deltaX) < 1) return;

      e.preventDefault();
      e.stopPropagation();

      accDeltaX.current += e.deltaX;

      if (wheelTimer.current) clearTimeout(wheelTimer.current);
      wheelTimer.current = setTimeout(() => { accDeltaX.current = 0; }, 100);

      if (accDeltaX.current > 40) {
        accDeltaX.current = 0;
        slide(1);
      } else if (accDeltaX.current < -40) {
        accDeltaX.current = 0;
        slide(-1);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelTimer.current) clearTimeout(wheelTimer.current);
    };
  }, [count, slide]);

  // ── Pointer drag (touch + click-and-drag) ──
  // Uses document-level move/up to avoid setPointerCapture stealing button clicks
  useEffect(() => {
    const el = containerRef.current;
    if (!el || count <= 1) return;

    let startX: number | null = null;
    let dragging = false;

    const onDown = (e: PointerEvent) => {
      // Don't start drag on buttons (arrows, action buttons, dots)
      if ((e.target as HTMLElement).closest('button')) return;
      if (e.button !== 0) return;
      startX = e.clientX;
      dragging = false;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    };

    const onMove = (e: PointerEvent) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 8) dragging = true;
      if (dragging) setDragOffset(dx);
    };

    const onUp = (e: PointerEvent) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      setDragOffset(0);
      if (dragging && Math.abs(dx) > 50) slide(dx < 0 ? 1 : -1);
      dragging = false;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };

    el.addEventListener('pointerdown', onDown);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [count, slide]);

  // ── Actions ──
  const handleOpenNotebook = useCallback((instanceId: string) => {
    sessionStorage.setItem('notebook_launch_params', JSON.stringify({ instance_id: instanceId }));
    navigate('/dashboard/notebook/new-session');
  }, [navigate]);

  const handleStop = useCallback(async (instanceId: string) => {
    if (showConfirmStop !== instanceId) {
      setShowConfirmStop(instanceId);
      return;
    }
    setTerminatingId(instanceId);
    setShowConfirmStop(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiUrl()}/api/compute/instances/${instanceId}/terminate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (resp.ok) await onInstanceTerminated?.();
    } catch (err) {
      console.error('Failed to terminate instance:', err);
      setTerminatingId(null);
    }
  }, [showConfirmStop, onInstanceTerminated]);

  // ── Render ──
  const active = sorted[currentIndex];
  if (!active) return null;

  const activeDisplay: InstanceDisplayState = terminatingId === active.instance_id
    ? 'terminating' : getInstanceDisplayState(active.status, active.terminal_ready);
  const isTerminating = activeDisplay === 'terminating';
  const isReady = activeDisplay === 'running';

  const estimatedCost = active.launch_time
    ? Math.max(
        ((Date.now() - new Date(active.launch_time).getTime()) / 3600000) * (active.hourly_cost || 0),
        (active.hourly_cost || 0) * (10 / 60)
      )
    : 0;

  if (isCollapsed) {
    return (
      <div className="px-2 py-3 border-b border-gray-800">
        <div
          className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center transition-colors ${
            isTerminating ? 'bg-red-900/30 border border-red-700/50'
              : isReady ? 'bg-green-900/30 border border-green-700/50 hover:bg-green-900/50 cursor-pointer'
              : 'bg-blue-900/30 border border-blue-700/50 hover:bg-blue-900/50'
          }`}
          onClick={isReady ? () => handleOpenNotebook(active.instance_id) : undefined}
          title={isTerminating
            ? `${active.gpu_type} terminating...`
            : isReady ? `${active.gpu_type} ready - $${estimatedCost.toFixed(2)} spent`
            : `${active.gpu_type} starting...`}
        >
          <div className="relative">
            <FiCpu className={`w-5 h-5 ${isTerminating ? 'text-red-400' : isReady ? 'text-green-400' : 'text-blue-400'}`} />
            {count > 1 && (
              <span className="absolute -bottom-1.5 -right-2 text-[9px] font-bold text-gray-300 bg-gray-800 rounded-full w-3.5 h-3.5 flex items-center justify-center">
                {count}
              </span>
            )}
            <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full animate-pulse ${
              isTerminating ? 'bg-red-500' : isReady ? 'bg-green-500' : 'bg-blue-500'
            }`} />
          </div>
        </div>
      </div>
    );
  }

  const hasMultiple = count > 1;

  // Track transform: slide position + live drag offset
  const basePercent = -(currentIndex * 100);
  const trackStyle: React.CSSProperties = dragOffset !== 0
    ? { transform: `translateX(calc(${basePercent}% + ${dragOffset}px))`, transition: 'none' }
    : { transform: `translateX(${basePercent}%)` };

  // Per-instance dot colors based on each instance's own status
  const dotStates = sorted.map(inst => {
    const ds: InstanceDisplayState = terminatingId === inst.instance_id
      ? 'terminating' : getInstanceDisplayState(inst.status, inst.terminal_ready);
    return getDotColor(ds);
  });

  return (
    <div
      ref={containerRef}
      className="mx-3 mb-4 select-none"
      style={{ touchAction: 'pan-y pinch-zoom' }}
    >
      {/* Nav row: arrows + dots — lives above the tiles */}
      {hasMultiple && (
        <div className="flex items-center justify-between px-1 mb-1.5">
          <button
            onClick={() => slide(-1)}
            disabled={currentIndex === 0}
            className={`p-1 transition-colors rounded ${
              currentIndex === 0
                ? 'text-gray-700 cursor-default'
                : 'text-gray-500 hover:text-white hover:bg-gray-800/50'
            }`}
          >
            <FiChevronLeft className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center gap-1.5">
            {sorted.map((_, i) => (
              <button
                key={sorted[i].instance_id}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === currentIndex ? `w-4 h-1.5 ${dotStates[i]}` : 'w-1.5 h-1.5 bg-gray-600 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => slide(1)}
            disabled={currentIndex === count - 1}
            className={`p-1 transition-colors rounded ${
              currentIndex === count - 1
                ? 'text-gray-700 cursor-default'
                : 'text-gray-500 hover:text-white hover:bg-gray-800/50'
            }`}
          >
            <FiChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Carousel viewport — clips tiles, each tile has its own border */}
      <div className="overflow-hidden rounded-lg">
        <div
          className={`flex ${dragOffset === 0 ? 'transition-transform duration-300 ease-out' : ''}`}
          style={trackStyle}
        >
          {sorted.map((inst) => (
            <InstanceTile
              key={inst.instance_id}
              instance={inst}
              terminatingId={terminatingId}
              showConfirmId={showConfirmStop}
              onOpenNotebook={handleOpenNotebook}
              onStop={handleStop}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
